import { useEffect } from "react";

import { Editor } from "@tiptap/react";

import { isTiptapContentEmpty } from "@demo/utils/editor";

/**
 * Hook to notify the parent component about content presence changes.
 *
 * Runs once per editor update, and only invokes the callback when a flag
 * actually flips — emitting a fresh object every keystroke would re-render
 * the whole compose input pane (and re-register its window keydown listener)
 * for no reason.
 */
export function useContentChangeNotification(
  editor: Editor | null,
  onHasEditorContent: ((hasContent: { text: boolean; media: boolean }) => void) | undefined,
) {
  useEffect(() => {
    if (!editor || !onHasEditorContent) return;

    let last: { text: boolean; media: boolean } | null = null;

    const checkContent = () => {
      const { text: isEmptyText, media: isEmptyMedia } = isTiptapContentEmpty(editor.getJSON());
      const next = { text: !isEmptyText, media: !isEmptyMedia };

      if (last && last.text === next.text && last.media === next.media) return;
      last = next;
      onHasEditorContent(next);
    };

    // Check on updates
    editor.on("update", checkContent);

    // Initial check
    checkContent();

    return () => {
      editor.off("update", checkContent);
    };
  }, [editor, onHasEditorContent]);
}
